import { useMemo, useState } from 'react';
import { Table, Badge, Card, Collapse } from 'react-bootstrap';
import { ChevronDown, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import type { OrderFulfillmentStatus } from '../../../../infrastructure/OMSClient';

interface FulfillmentStatusTableProps {
  items: OrderFulfillmentStatus[];
  viewMode: 'sla' | 'pic';
  isLoading: boolean;
}

interface PICGroup {
  pic: string;
  items: OrderFulfillmentStatus[];
  withinSLA: number;
  atRisk: number;
  breached: number;
  completed: number;
}

type SortField =
  | 'orderId'
  | 'orderDate'
  | 'country'
  | 'pic'
  | 'slaStatus'
  | 'elapsedDays'
  | 'remainingDays'
  | 'targetDays'
  | 'amount';
type SortDirection = 'asc' | 'desc' | null;

export function FulfillmentStatusTable({ items, viewMode, isLoading }: FulfillmentStatusTableProps) {
  const [expandedPICs, setExpandedPICs] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortField(null);
        setSortDirection(null);
      }
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedItems = useMemo(() => {
    const itemsCopy = [...items];

    if (!sortField || !sortDirection) {
      const statusOrder: Record<string, number> = { BREACHED: 0, AT_RISK: 1, WITHIN_SLA: 2 };
      return itemsCopy.sort((a, b) => {
        const statusA = statusOrder[a.slaResult.status] ?? 3;
        const statusB = statusOrder[b.slaResult.status] ?? 3;
        if (statusA !== statusB) {
          return statusA - statusB;
        }
        return a.slaResult.remainingDays - b.slaResult.remainingDays;
      });
    }

    return itemsCopy.sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'orderId':
          comparison = a.order.orderId.localeCompare(b.order.orderId);
          break;
        case 'orderDate':
          comparison = new Date(a.order.orderDate).getTime() - new Date(b.order.orderDate).getTime();
          break;
        case 'country':
          comparison = a.order.countryCode.localeCompare(b.order.countryCode);
          break;
        case 'pic':
          comparison = (a.order.pic || '').localeCompare(b.order.pic || '');
          break;
        case 'slaStatus':
          const statusOrder: Record<string, number> = { BREACHED: 0, AT_RISK: 1, WITHIN_SLA: 2 };
          comparison = (statusOrder[a.slaResult.status] ?? 3) - (statusOrder[b.slaResult.status] ?? 3);
          break;
        case 'elapsedDays':
          comparison = a.slaResult.elapsedDays - b.slaResult.elapsedDays;
          break;
        case 'remainingDays':
          comparison = a.slaResult.remainingDays - b.slaResult.remainingDays;
          break;
        case 'targetDays':
          comparison = a.slaResult.targetDays - b.slaResult.targetDays;
          break;
        case 'amount':
          comparison = a.order.totalAmount - b.order.totalAmount;
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [items, sortField, sortDirection]);

  const picGroups = useMemo(() => {
    const groups = new Map<string, OrderFulfillmentStatus[]>();

    items.forEach((item) => {
      const pic = item.order.pic || 'Unassigned';
      if (!groups.has(pic)) {
        groups.set(pic, []);
      }
      groups.get(pic)!.push(item);
    });

    const picGroupsArray: PICGroup[] = Array.from(groups.entries()).map(([pic, picItems]) => {
      const statusOrder: Record<string, number> = { BREACHED: 0, AT_RISK: 1, WITHIN_SLA: 2 };
      const sortedItems = picItems.sort((a, b) => {
        const statusA = statusOrder[a.slaResult.status] ?? 3;
        const statusB = statusOrder[b.slaResult.status] ?? 3;
        if (statusA !== statusB) {
          return statusA - statusB;
        }
        return a.slaResult.remainingDays - b.slaResult.remainingDays;
      });

      return {
        pic,
        items: sortedItems,
        withinSLA: picItems.filter((i) => i.slaResult.status === 'WITHIN_SLA').length,
        atRisk: picItems.filter((i) => i.slaResult.status === 'AT_RISK').length,
        breached: picItems.filter((i) => i.slaResult.status === 'BREACHED').length,
        completed: 0,
      };
    });

    return picGroupsArray.sort((a, b) => {
      if (a.breached !== b.breached) {
        return b.breached - a.breached;
      }
      if (a.atRisk !== b.atRisk) {
        return b.atRisk - a.atRisk;
      }
      return a.pic.localeCompare(b.pic);
    });
  }, [items]);

  const togglePIC = (pic: string) => {
    setExpandedPICs((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(pic)) {
        newSet.delete(pic);
      } else {
        newSet.add(pic);
      }
      return newSet;
    });
  };

  const getSLAStatusVariant = (status: string) => {
    switch (status) {
      case 'BREACHED':
        return 'danger';
      case 'AT_RISK':
        return 'warning';
      case 'WITHIN_SLA':
        return 'success';
      default:
        return 'secondary';
    }
  };

  const getSLAStatusText = (status: string) => {
    switch (status) {
      case 'BREACHED':
        return 'Breached';
      case 'AT_RISK':
        return 'At Risk';
      case 'WITHIN_SLA':
        return 'Within SLA';
      default:
        return status;
    }
  };

  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown size={14} className="ms-1 text-muted" />;
    }
    if (sortDirection === 'asc') {
      return <ArrowUp size={14} className="ms-1" />;
    }
    return <ArrowDown size={14} className="ms-1" />;
  };

  const SortableHeader = ({
    field,
    children,
    className = '',
  }: {
    field: SortField;
    children: React.ReactNode;
    className?: string;
  }) => (
    <th onClick={() => handleSort(field)} style={{ cursor: 'pointer', userSelect: 'none' }} className={className}>
      <div className="d-flex align-items-center">
        {children}
        {renderSortIcon(field)}
      </div>
    </th>
  );

  const renderOrderRow = (item: OrderFulfillmentStatus) => (
    <tr key={item.order.orderId} className="table-hover">
      <td className="fw-medium">
        <a
          href={`/oms/orders/${item.order.orderId}`}
          className="text-primary text-decoration-none"
          style={{ display: 'block', cursor: 'pointer' }}
          target="_blank"
          rel="noopener noreferrer"
        >
          {item.order.orderId}
        </a>
      </td>
      <td>{new Date(item.order.orderDate).toLocaleDateString()}</td>
      <td>{item.order.countryCode}</td>
      <td>{item.order.pic || '-'}</td>
      <td>
        <Badge bg="light" text="dark">
          {item.slaResult.orderStatus}
        </Badge>
      </td>
      <td>
        <Badge bg={getSLAStatusVariant(item.slaResult.status)}>{getSLAStatusText(item.slaResult.status)}</Badge>
      </td>
      <td className="text-end">{item.slaResult.elapsedDays}</td>
      <td className="text-end">
        <span
          className={
            item.slaResult.remainingDays < 0
              ? 'text-danger fw-bold'
              : item.slaResult.remainingDays <= 2
                ? 'text-warning fw-bold'
                : ''
          }
        >
          {item.slaResult.remainingDays}
        </span>
      </td>
      <td className="text-end">{item.slaResult.targetDays}</td>
      <td className="text-end fw-medium">
        {item.order.currency} {item.order.totalAmount != null ? item.order.totalAmount.toFixed(2) : 'N/A'}
      </td>
    </tr>
  );

  if (isLoading) {
    return <div className="text-center py-5">Loading...</div>;
  }

  if (viewMode === 'pic') {
    return (
      <div className="d-flex flex-column gap-2">
        {picGroups.map((group) => {
          const isExpanded = expandedPICs.has(group.pic);
          return (
            <Card key={group.pic}>
              <Card.Header
                style={{ cursor: 'pointer' }}
                onClick={() => togglePIC(group.pic)}
                className="d-flex align-items-center justify-content-between bg-light"
              >
                <div className="d-flex align-items-center gap-3">
                  {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                  <span className="fw-bold fs-5">{group.pic}</span>
                  <span className="text-muted">({group.items.length} orders)</span>
                </div>
                <div className="d-flex gap-2">
                  <Badge bg="success">Within SLA: {group.withinSLA}</Badge>
                  <Badge bg="warning" text="dark">
                    At Risk: {group.atRisk}
                  </Badge>
                  <Badge bg="danger">Breached: {group.breached}</Badge>
                </div>
              </Card.Header>
              <Collapse in={isExpanded}>
                <div>
                  <div style={{ overflowX: 'auto' }}>
                    <Table striped hover responsive>
                      <thead>
                        <tr>
                          <th>Order ID</th>
                          <th>Order Date</th>
                          <th>Country</th>
                          <th>PIC</th>
                          <th>Order Status</th>
                          <th>SLA Status</th>
                          <th className="text-end">Elapsed Days</th>
                          <th className="text-end">Remaining Days</th>
                          <th className="text-end">Target Days</th>
                          <th className="text-end">Total Amount</th>
                        </tr>
                      </thead>
                      <tbody>{group.items.map((item) => renderOrderRow(item))}</tbody>
                    </Table>
                  </div>
                </div>
              </Collapse>
            </Card>
          );
        })}
        {picGroups.length === 0 && (
          <div className="text-center py-5 text-muted">No orders found matching the criteria.</div>
        )}
      </div>
    );
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <Table striped hover responsive>
        <thead>
          <tr>
            <SortableHeader field="orderId">Order ID</SortableHeader>
            <SortableHeader field="orderDate">Order Date</SortableHeader>
            <SortableHeader field="country">Country</SortableHeader>
            <SortableHeader field="pic">PIC</SortableHeader>
            <th>Order Status</th>
            <SortableHeader field="slaStatus">SLA Status</SortableHeader>
            <SortableHeader field="elapsedDays" className="text-end">
              Elapsed Days
            </SortableHeader>
            <SortableHeader field="remainingDays" className="text-end">
              Remaining Days
            </SortableHeader>
            <SortableHeader field="targetDays" className="text-end">
              Target Days
            </SortableHeader>
            <SortableHeader field="amount" className="text-end">
              Total Amount
            </SortableHeader>
          </tr>
        </thead>
        <tbody>{sortedItems.map((item) => renderOrderRow(item))}</tbody>
      </Table>
      {sortedItems.length === 0 && (
        <div className="text-center py-5 text-muted">No orders found matching the criteria.</div>
      )}
    </div>
  );
}
